import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { 
  calculateOverallScore,
  extractQualityScores,
  getTitleQualityMessage,
  getDescriptionQualityMessage,
  getDescriptionBulletsQualityMessage,
  getImageQualityMessage,
  getSeoQualityMessage,
  getAltTagsQualityMessage,
  getCategoryQualityMessage,
  getProductTagsQualityMessage,
  calculateAverageQualityScore,
  getEstimatedImpact,
  getQualityMessage
} from "./utils";
import { ProductQualityMetric } from "../../types/api";
import { QualityScore } from "../../types/api";
import getPriorities from "./getPriorities";
import { LucideProps } from "lucide-react";
import getRecommendedActions from "./getRecommendedActions";
import i18next from "i18next";

export type SuggestedAction = {
  subtext: string;
  title: string;
  score: number;
  action: string;
  enrichmentFeatures: string[];
  icon: React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;
  count: number;
  threshold: number;
  priority: number;
}

export interface ScoreState {
  enrichments: Record<
    string,
    {
      products: ProductQualityMetric[];
    }
  >;
  enrichmentQualityScores: Record<string, {
    base: QualityScore;
    calculated: {
      overallBefore: number;
      overallAfter: number;
    }
  }>;
  suggestedActions: SuggestedAction[] | null;
  initialQualityScore: QualityScore | null;
  averageQualityScore: number | null;
  estimatedImpact: string | null;
  titleQualityScore: number | null;
  descriptionQualityScore: number | null;
  descriptionBulletsQualityScore: number | null;  
  imageQualityScore: number | null;
  seoQualityScore: number | null;
  altTagsQualityScore: number | null;
  tagsScore: number | null;
  categoryQualityScore: number | null;
  qualityMessage: string;
  industryAverage: number;
  priorities: {
    type: string;
    icon: React.ForwardRefExoticComponent<
      Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
    >;
    metricTitle: string;
    score: number;
    title: string;
    message: string | null;
    color: string;
  }[];
  dashboardTitleQualityMessage: string;
  dasboardDescriptionQualityMessage: string;
  dashboardDescriptionBulletsQualityMessage: string;  
  dashboardImageQualityMessage: string;
  dashboardSeoQualityMessage: string;
  dashboardAltTagsQualityMessage: string;
  dashboardCategoryQualityMessage: string;
  dashboardProductTagsQualityMessage: string;
  initialMailScores: {
    overallScore: number;
    competitorAvg: number;
    conversionScore: number;
    discoveryScore: number;
    searchScore: number;
    titleScore: number;
    descriptionScore: number;
    imageScore: number;
    seoScore: number;
    altTagsScore: number;
    categoryScore: number;
  } | null;
  initialMailFlow: {
    sent: boolean; 
  } | null;
}

const initialState: ScoreState = {
  enrichments: {},
  enrichmentQualityScores: {},
  initialQualityScore: null,
  industryAverage: 65,
  averageQualityScore: null,
  estimatedImpact: null,
  qualityMessage: "",
  priorities: [],
  titleQualityScore: null,
  descriptionQualityScore: null,
  descriptionBulletsQualityScore: null,
  dashboardDescriptionBulletsQualityMessage: "",
  imageQualityScore: null,
  seoQualityScore: null,
  altTagsQualityScore: null,
  categoryQualityScore: null,
  tagsScore: null,
  dashboardTitleQualityMessage: "",
  dasboardDescriptionQualityMessage: "",
  dashboardImageQualityMessage: "",
  dashboardSeoQualityMessage: "",
  dashboardAltTagsQualityMessage: "",
  dashboardCategoryQualityMessage: "",
  dashboardProductTagsQualityMessage: "",
  initialMailScores: null,
  initialMailFlow: null,
  suggestedActions: null
};

export const scoreSlice = createSlice({
  name: "score",
  initialState,
  reducers: {
    setEnrichment: (
      state,
      action: PayloadAction<{
        enrichmentJobId: string;
        products: ProductQualityMetric[];
      }>
    ) => {
      state.enrichments[action.payload.enrichmentJobId] = {
        products: action.payload.products,
      };
    },
    setEnrichmentQualityScore: (
      state,
      action: PayloadAction<{enrichmentJobId: string, qualityScore: QualityScore}>
    ) => {
      const score = action.payload.qualityScore;
      
      const before = calculateOverallScore({
        category_placement_score: score.old_total_category_placement_score,
        desc_html_score: score.old_total_desc_html_score,
        alt_text_score: score.old_total_alt_text_score,
        images_count_score: score.old_total_images_count_score,
        title_score: score.old_total_title_score,
        seo_description_score: score.old_total_seo_description_score,
        tags_score: score.old_total_tags_score,
        seo_title_score: score.old_total_seo_title_score
      });

      const after = calculateOverallScore({
        category_placement_score: score.total_category_placement_score,
        desc_html_score: score.total_desc_html_score,
        alt_text_score: score.total_alt_text_score,
        images_count_score: score.total_images_count_score,
        title_score: score.total_title_score,
        seo_description_score: score.total_seo_description_score,
        tags_score: score.total_tags_score,
        seo_title_score: score.total_seo_title_score
      });

      state.enrichmentQualityScores[action.payload.enrichmentJobId] = {
        base: action.payload.qualityScore,
        calculated: {
          overallBefore: before,
          overallAfter: after
        }
      };
    },
    setInitialMailFlow: (state, action: PayloadAction<{sent: boolean}>) => {
      state.initialMailFlow = action.payload;
    },
    setInitialQualityScore: (state, action: PayloadAction<QualityScore>) => {
      try {
        const actions = getRecommendedActions(action.payload);
        state.suggestedActions = actions;
      } catch(err) {
        console.log(err);
      }

      state.initialQualityScore = action.payload;
      
      // Extract and set all quality scores
      const scores = extractQualityScores(action.payload);
      Object.assign(state, scores);

      // Set quality messages using utility functions
      state.dashboardTitleQualityMessage = getTitleQualityMessage(scores.titleQualityScore);
      state.dasboardDescriptionQualityMessage = getDescriptionQualityMessage(scores.descriptionQualityScore);
      state.dashboardDescriptionBulletsQualityMessage = getDescriptionBulletsQualityMessage(scores.descriptionBulletsQualityScore);
      state.dashboardImageQualityMessage = getImageQualityMessage(scores.imageQualityScore);
      state.dashboardSeoQualityMessage = getSeoQualityMessage(scores.seoQualityScore);
      state.dashboardAltTagsQualityMessage = getAltTagsQualityMessage(scores.altTagsQualityScore);
      state.dashboardCategoryQualityMessage = getCategoryQualityMessage(scores.categoryQualityScore);
      state.dashboardProductTagsQualityMessage = getProductTagsQualityMessage(scores.tagsScore);

      // Calculate average quality score and set related messages
      state.averageQualityScore = calculateAverageQualityScore(scores, action.payload.total_seo_title_score);
      state.estimatedImpact = getEstimatedImpact(state.averageQualityScore);
      state.qualityMessage = getQualityMessage(state.averageQualityScore);

      // Set priorities
      state.priorities = getPriorities({
        titleQuality: scores.titleQualityScore,
        descriptionQuality: scores.descriptionQualityScore,
        imageQuality: scores.imageQualityScore,
        seoQuality: scores.seoQualityScore,
        altTagsQuality: scores.altTagsQualityScore,
        categoryQuality: scores.categoryQualityScore,
        t: i18next.t.bind(i18next)
      });

      // Set mail scores
      state.initialMailScores = {
        overallScore: state.averageQualityScore,
        competitorAvg: 45,
        conversionScore: Math.round((scores.titleQualityScore + scores.descriptionQualityScore + scores.imageQualityScore) / 3),
        discoveryScore: Math.round((scores.seoQualityScore + scores.altTagsQualityScore) / 2),
        searchScore: Math.round((scores.categoryQualityScore + scores.tagsScore) / 2),
        titleScore: scores.titleQualityScore,
        descriptionScore: scores.descriptionQualityScore,
        imageScore: scores.imageQualityScore,
        seoScore: scores.seoQualityScore,
        altTagsScore: scores.altTagsQualityScore,
        categoryScore: scores.categoryQualityScore
      }
    },
  },
});

export const { setEnrichment, setInitialQualityScore, setEnrichmentQualityScore, setInitialMailFlow } = scoreSlice.actions;
export default scoreSlice.reducer;
